import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import LookupPicker from '@/components/LookupPicker';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { PAYMENT_KINDS } from '@/shared/constants';
import { ORDER_FIELDS } from '@/shared/fields';
import { PAYMENT_METHODS, PLAN_VALUES, methodValuesWith, money, orderTotal, renewalPrice } from '@/shared/pricing';
import { committed, idOf, logActivity, todayDate } from '@/shared/data';

const METHOD_LABELS = Object.fromEntries(PAYMENT_METHODS.map((m) => [m.value, m.label]));

function fromOrder(order) {
  const kind = order?.status_c === 'Active' ? 'Renewal' : 'New';
  const amount = kind === 'Renewal' ? renewalPrice(order) : orderTotal(order);
  return {
    Name: order ? `${order.Name} · ${kind}` : '',
    order_c: order?.Id ?? null,
    plan_c: order?.plan_c || 'Demon',
    kind_c: kind,
    amount_c: order ? String(amount) : '',
    method_c: order?.payment_method_c || 'Card',
    paid_on_c: todayDate(),
  };
}

function fromPayment(p) {
  return {
    Name: p.Name ?? '',
    order_c: idOf(p.order_c),
    plan_c: p.plan_c || 'Demon',
    kind_c: p.kind_c || 'New',
    amount_c: p.amount_c == null ? '' : String(p.amount_c),
    method_c: p.method_c || 'Card',
    paid_on_c: p.paid_on_c ? String(p.paid_on_c).slice(0, 10) : todayDate(),
  };
}

/** Record or edit a payment. Pass `order` to lock it to one order, `payment` to edit. */
export default function PaymentDialog({ open, onOpenChange, payment, order, onSaved }) {
  const editing = !!payment;
  const [form, setForm] = useState(fromOrder(order));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(payment ? fromPayment(payment) : fromOrder(order));
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  async function pickOrder(id) {
    set('order_c')(id || null);
    if (!id || editing) return;
    const r = await sdk.table('orders_c').select(ORDER_FIELDS).get(id);
    if (r?.data) {
      const next = fromOrder(r.data);
      setForm((f) => ({ ...next, paid_on_c: f.paid_on_c }));
    }
  }

  async function submit() {
    const amount = Number(form.amount_c);
    if (!form.Name.trim()) {
      toast.error('Add a reference, like the order code and kind');
      return;
    }
    if (!form.order_c) {
      toast.error('Pick the order this payment is for');
      return;
    }
    if (!(amount > 0)) {
      toast.error('Enter the amount received');
      return;
    }
    const fields = {
      Name: form.Name.trim(),
      order_c: form.order_c,
      plan_c: form.plan_c,
      kind_c: form.kind_c,
      amount_c: amount,
      method_c: form.method_c,
      paid_on_c: form.paid_on_c || todayDate(),
    };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('payments_c').update({ Id: payment.Id, ...fields })
        : await sdk.table('payments_c').create(fields);
      const saved = committed(res, editing ? 'Payment updated' : 'Payment recorded');
      if (!saved) return;
      if (!editing) logActivity('Payment', `Recorded ${money(amount)}`, fields.Name, METHOD_LABELS[fields.method_c] ?? fields.method_c);
      onOpenChange(false);
      setForm(fromOrder(order));
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit payment' : 'Record a payment'}
      description={editing ? undefined : 'Log money you have actually received. Nothing is charged from here.'}
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Record payment'}
      busyLabel={editing ? 'Saving…' : 'Recording…'}
      wide
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Reference" htmlFor="pay-name">
          <Input
            id="pay-name"
            value={form.Name}
            onChange={(e) => set('Name')(e.target.value)}
            placeholder="GC-M4Y7QK · Renewal"
          />
        </Field>
        <Field label="Order" htmlFor="pay-order">
          <LookupPicker
            table="orders_c"
            searchField="Name"
            value={form.order_c}
            onChange={pickOrder}
            placeholder="Search order codes…"
            clearable={!order}
          />
        </Field>
        <Field label="Kind" htmlFor="pay-kind">
          <PickSelect id="pay-kind" value={form.kind_c} onChange={set('kind_c')} values={PAYMENT_KINDS} placeholder="New or renewal" />
        </Field>
        <Field label="Plan" htmlFor="pay-plan">
          <PickSelect id="pay-plan" value={form.plan_c} onChange={set('plan_c')} values={PLAN_VALUES} placeholder="Which plan" />
        </Field>
        <Field label="Amount (USD)" htmlFor="pay-amount">
          <Input
            id="pay-amount"
            type="number"
            min={0}
            step="0.01"
            value={form.amount_c}
            onChange={(e) => set('amount_c')(e.target.value)}
            placeholder="56"
          />
        </Field>
        <Field label="Method" htmlFor="pay-method">
          <PickSelect
            id="pay-method"
            value={form.method_c}
            onChange={set('method_c')}
            values={methodValuesWith(form.method_c)}
            labels={METHOD_LABELS}
            placeholder="How they paid"
          />
        </Field>
        <Field label="Paid on" htmlFor="pay-date">
          <Input
            id="pay-date"
            type="date"
            value={form.paid_on_c}
            onChange={(e) => set('paid_on_c')(e.target.value)}
            placeholder="2026-11-02"
          />
        </Field>
      </div>
    </FormDialog>
  );
}
